<template>
  <div class="admin-page">
    <CommonModal
      modal-id="adminErrorModal"
      title="Error"
      :message="errorModalMessage"
      :buttons="errorModalButtons"
    />
    <CommonModal
      modal-id="adminResetModal"
      title="Reset Search Conditions"
      :message="resetModalMessage"
      :buttons="resetModalButtons"
    />
    <div class="admin-container">
      <header class="page-header">
        <div class="page-header__title">
          <h1>Checklist Reference</h1>
          <p class="page-header__sub">Distributor: {{ distributorName }}</p>
        </div>
      </header>

      <AdminSearchFilter
        ref="searchFilterRef"
        :is-loading="isLoading"
        @search="onSearch"
        @reset="onResetClick"
      />

      <AdminResultTable
        v-if="hasSearched"
        ref="resultTableRef"
        :rows="rows"
        :is-loading="isLoading"
        :current-page="currentPage"
        :can-go-prev="canGoPrev"
        :can-go-next="canGoNext"
        :selected-ids="selectedIds"
        :is-all-selected="isAllSelected"
        :sort-by="sortBy"
        :sort-order="sortOrder"
        :is-csv-download-disabled="isCsvDownloadDisabled"
        @prev="goToPrev"
        @next="goToNext"
        @sort="onSortClick"
        @toggle-select-all="toggleSelectAll"
        @toggle-row="toggleRow"
        @images-click="onImagesClick"
        @download-csv="onDownloadCsv"
      />
    </div>

    <ChecklistImageModal
      :row="selectedRow"
      :is-open="showImageModal"
      @close="closeImageModal"
    />
  </div>
</template>

<script>
import { computed, ref, nextTick } from "vue";
import { useStore } from "vuex";
import * as bootstrap from "bootstrap";
import McusDataService from "../services/McusDataService";
import { toJstYmdHmsFromUtcYmdHms } from "../utils/dateUtils";
import CommonModal from "./CommonModal.vue";
import AdminSearchFilter from "./AdminSearchFilter.vue";
import AdminResultTable from "./AdminResultTable.vue";
import ChecklistImageModal from "./ChecklistImageModal.vue";

async function apiPostSearchChecklist(requestBody) {
  const res = await McusDataService.searchChecklist(requestBody ?? {});
  return {
    status: res.status,
    data: res.data ?? {},
  };
}

function openModalById(modalId, fallbackMessage) {
  try {
    const el = document.getElementById(modalId);
    if (!el) return;
    const modal = bootstrap.Modal.getOrCreateInstance(el);
    modal.show();
  } catch (_e) {
    const msg = String(fallbackMessage ?? "").trim();
    if (msg) window.alert(msg);
  }
}

export default {
  name: "AdminPage",
  components: { CommonModal, AdminSearchFilter, AdminResultTable, ChecklistImageModal },
  setup() {
    const store = useStore();

    // ---- Error modal ----
    const errorModalMessage = ref("");
    const errorModalButtons = [
      {
        label: "OK",
        class: "btn btn-primary",
        onClick: () => {},
      },
    ];

    // ---- Reset confirmation modal ----
    const resetModalMessage = ref("Are you sure you want to reset all search conditions?");
    const resetModalButtons = computed(() => [
      {
        label: "Cancel",
        class: "btn btn-secondary",
        onClick: () => {},
      },
      {
        label: "Reset",
        class: "btn btn-danger",
        onClick: () => {
          searchFilterRef.value?.clearAll();
          hasSearched.value = false;
          rows.value = [];
          nextStartKey.value = null;
          startKeyStack.value = [null];
          currentPage.value = 1;
          selectedRowMap.value = new Map();
          committedFilters.value = undefined;
        },
      },
    ]);

    // ---- Distributor name ----
    const companyCode = computed(() => store.getters.companyCode);
    const companyName = computed(() => store.getters.companyName);
    const distributorName = computed(() => {
      return (
        String(companyName.value ?? "") ||
        String(companyCode.value ?? "")
      );
    });

    // ---- Refs ----
    const searchFilterRef = ref(null);
    const resultTableRef = ref(null);

    // ---- Result state ----
    const hasSearched = ref(false);
    const rows = ref([]);
    const nextStartKey = ref(null);
    const startKeyStack = ref([null]);
    const currentPage = ref(1);
    const selectedRowMap = ref(new Map());

    const selectedIds = computed(() => new Set(selectedRowMap.value.keys()));

    const canGoPrev = computed(() => startKeyStack.value.length > 1);
    const canGoNext = computed(() => nextStartKey.value != null);

    const isAllSelected = computed(() => {
      const list = rows.value;
      if (list.length === 0) return false;
      return list.every((r) => selectedRowMap.value.has(r.uniqueId));
    });

    const toExportableRow = (row) => ({
      vin: row?.vin ?? "",
      uploadDate: row?.uploadDate ?? "",
      distributorCode: row?.distributorCode ?? "",
      modelName: row?.modelName ?? "",
    });

    const addRowsToSelection = (rowsToAdd = []) => {
      const next = new Map(selectedRowMap.value);
      rowsToAdd.forEach((row) => {
        if (!row || !row.uniqueId) return;
        next.set(row.uniqueId, toExportableRow(row));
      });
      selectedRowMap.value = next;
    };

    const removeRowsFromSelection = (rowsToRemove = []) => {
      if (!rowsToRemove.length) return;
      const next = new Map(selectedRowMap.value);
      rowsToRemove.forEach((row) => {
        if (!row || !row.uniqueId) return;
        next.delete(row.uniqueId);
      });
      selectedRowMap.value = next;
    };

    const toggleSelectAll = (checked, pageRows = []) => {
      if (checked) {
        addRowsToSelection(pageRows);
      } else {
        removeRowsFromSelection(pageRows);
      }
    };

    const toggleRow = (row, checked) => {
      if (!row || !row.uniqueId) return;
      if (checked) {
        addRowsToSelection([row]);
      } else {
        removeRowsFromSelection([row]);
      }
    };

    // ---- Committed filters (fixed at Search time) ----
    const committedFilters = ref(undefined);

    // ---- Sort state ----
    const sortBy = ref("vin");     // "vin" | "registerDate"
    const sortOrder = ref("desc"); // "asc" | "desc"

    const buildSort = () => ({
      by: sortBy.value,
      order: sortOrder.value,
    });

    // ---- Loading ----
    const isLoading = ref(false);

    // ---- Scroll helper ----
    const scrollResultsToTop = async () => {
      await nextTick();
      resultTableRef.value?.scrollToTop?.();
    };

    // ---- Error display ----
    const showFetchError = (status) => {
      if (status == null) {
        errorModalMessage.value = "A network error occurred. Please check your connection and try again.";
        openModalById("adminErrorModal", errorModalMessage.value);
        return;
      }
      if (status >= 400 && status < 500) {
        errorModalMessage.value = `(${status}) Request error. Please check your input and permissions.`;
        openModalById("adminErrorModal", errorModalMessage.value);
        return;
      }
      if (status >= 500 && status < 600) {
        errorModalMessage.value = `(${status}) Server error. Please try again later.`;
        openModalById("adminErrorModal", errorModalMessage.value);
        return;
      }
      errorModalMessage.value = `An error occurred.`;
      openModalById("adminErrorModal", errorModalMessage.value);
    };

    // ---- API call ----
    const fetchPage = async (startKey, filters) => {
      if (isLoading.value) return { ok: false };
      isLoading.value = true;

      const requestBody = {};
      if (startKey != null) requestBody.startKey = startKey;
      if (filters) requestBody.filters = filters;
      requestBody.sort = buildSort();

      try {
        const res = await apiPostSearchChecklist(requestBody);

        if (res?.status == null) { showFetchError(null); return { ok: false }; }
        if (res.status >= 400 && res.status < 600) { showFetchError(res.status); return { ok: false }; }

        const mappedRows = (res.data.data ?? []).map((r) => {
          const vin = r.vin ?? "";
          const utcUploadDate = r.uploadDate ?? "";
          return {
            vin,
            distributorCode: r.distributorCode ?? "",
            modelName: r.modelName ?? "",
            checklistImage: r.checklistImage ?? "",
            uploadDate: toJstYmdHmsFromUtcYmdHms(utcUploadDate),
            uniqueId: `${vin}-${utcUploadDate}`,
            __utcUploadDate: utcUploadDate,
          };
        });

        return { ok: true, rows: mappedRows, nextStartKey: res.data.nextStartKey ?? null };
      } catch (e) {
        const status = e?.response?.status;
        const axiosCode = e?.code;
        const isNetworkError = status == null && typeof axiosCode === "string" && axiosCode.length > 0;
        showFetchError(isNetworkError ? null : status);
        return { ok: false };
      } finally {
        isLoading.value = false;
      }
    };

    // ---- Shared page reset helper ----
    const applyPageResult = async (result) => {
      currentPage.value = 1;
      startKeyStack.value = [null];
      rows.value = result.rows;
      nextStartKey.value = result.nextStartKey;
      await scrollResultsToTop();
    };

    // ---- Handlers ----

    /** AdminSearchFilter から emit("search", filters) で呼ばれる */
    const onSearch = async (filters) => {
      committedFilters.value = filters;
      selectedRowMap.value = new Map();

      const result = await fetchPage(null, committedFilters.value);
      if (!result.ok) return;

      hasSearched.value = true;
      await applyPageResult(result);
    };

    /** AdminSearchFilter から emit("reset") で呼ばれる → 確認モーダルを表示 */
    const onResetClick = () => {
      openModalById("adminResetModal", "Are you sure you want to reset all search conditions?");
    };

    /**
     * AdminResultTable から emit("sort", column) で呼ばれる。
     * - 同じカラム: asc ↔ desc トグル
     * - 別のカラム: そのカラムの desc から開始
     */
    const onSortClick = async (column) => {
      if (!hasSearched.value || isLoading.value) return;

      // 現在の値を保存
      const originalSortBy = sortBy.value;
      const originalSortOrder = sortOrder.value;

      // 新しいソート値を設定
      if (sortBy.value === column) {
        sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
      } else {
        sortBy.value = column;
        sortOrder.value = "desc";
      }

      const result = await fetchPage(null, committedFilters.value);
      
      if (!result.ok) {
        // エラーの場合、元の値に戻す
        sortBy.value = originalSortBy;
        sortOrder.value = originalSortOrder;
        return;
      }

      await applyPageResult(result);
    };

    const goToNext = async () => {
      if (!canGoNext.value) return;

      const nextKey = nextStartKey.value;
      const result = await fetchPage(nextKey, committedFilters.value);
      if (!result.ok) return;

      startKeyStack.value = [...startKeyStack.value, nextKey];
      currentPage.value += 1;
      rows.value = result.rows;
      nextStartKey.value = result.nextStartKey;

      await scrollResultsToTop();
    };

    const goToPrev = async () => {
      if (!canGoPrev.value) return;

      const nextStack = startKeyStack.value.slice(0, -1);
      const prevKey = nextStack[nextStack.length - 1];

      const result = await fetchPage(prevKey, committedFilters.value);
      if (!result.ok) return;

      startKeyStack.value = nextStack;
      currentPage.value -= 1;
      rows.value = result.rows;
      nextStartKey.value = result.nextStartKey;

      await scrollResultsToTop();
    };

    const isCsvDownloading = ref(false);
    const isCsvDownloadDisabled = computed(() => selectedRowMap.value.size === 0 || isCsvDownloading.value);

    const escapeCsvField = (value) => {
      const str = String(value ?? "");
      if (/[",\n\r]/.test(str)) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    };

    const buildCsvContent = () => {
      if (selectedRowMap.value.size === 0) return null;

      const headers = ["VIN", "Upload Date (JST)", "Distributor Code", "Model Name"];
      const lines = [headers.map(escapeCsvField).join(",")];

      selectedRowMap.value.forEach((data) => {
        const fields = [
          escapeCsvField(data.vin),
          escapeCsvField(data.uploadDate),
          escapeCsvField(data.distributorCode),
          escapeCsvField(data.modelName),
        ];
        lines.push(fields.join(","));
      });

      return "\ufeff" + lines.join("\r\n");
    };

    const onDownloadCsv = () => {
      const csvContent = buildCsvContent();
      if (!csvContent) return;

      isCsvDownloading.value = true;
      setTimeout(() => {
        isCsvDownloading.value = false;
      }, 1000);

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const now = new Date();
      const jst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
      const ts = jst.toISOString().replace(/[-:T]/g, "").slice(0, 14);
      const timestamp = ts.slice(0, 8) + "_" + ts.slice(8, 14);
      link.href = url;
      link.setAttribute("download", `checklist_export_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    // ---- Image modal ----
    const selectedRow = ref(null);
    const showImageModal = ref(false);

    const onImagesClick = (row) => {
      selectedRow.value = row;
      showImageModal.value = true;
    };

    const closeImageModal = () => {
      showImageModal.value = false;
      selectedRow.value = null;
    };

    return {
      distributorName,
      errorModalMessage,
      errorModalButtons,
      resetModalMessage,
      resetModalButtons,
      isLoading,
      searchFilterRef,
      resultTableRef,
      hasSearched,
      currentPage,
      rows,
      canGoPrev,
      canGoNext,
      selectedIds,
      isCsvDownloading,
      isCsvDownloadDisabled,
      isAllSelected,
      sortBy,
      sortOrder,
      onSearch,
      onResetClick,
      onSortClick,
      goToPrev,
      goToNext,
      toggleSelectAll,
      toggleRow,
      onDownloadCsv,
      onImagesClick,
      selectedRow,
      showImageModal,
      closeImageModal,
    };
  },
};
</script>

<style scoped>
.admin-page {
  min-height: 100vh;
  background: #f9fafb;
}

.admin-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px;
}

.page-header {
  margin: 8px 0 16px;
}

.page-header__title h1 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  letter-spacing: 0;
}

.page-header__sub {
  margin: 6px 0 0;
  font-size: 13px;
  color: #6b7280;
}

@media (max-width: 640px) {
  .admin-container {
    padding: 16px;
  }
}
</style>
